import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { PATH } from '../../../constants/path';
import { STATIC_ROOT, APP_NAME } from '../../../constants';
import { FUNNEL_ADDRESS_STEP } from '../../../constants/funnel';
import { MODAL_TYPE_LOGIN } from '../../../services/modal/constants';

import { resetKyc } from '../../../services/kyc/actions';
import { showModal } from '../../../services/modal/actions';
import { resetLock, resetOrder } from '../../../services/ebon/actions';
import { fetchUserBalance } from '../../../services/account/balance/actions';
import { logoutUser } from '../../../services/authentication/logout/actions';

import { getCashbackAmount } from '../../../utils/ebonUtils';
import { getMerchantCardUrl } from '../../../services/partner/utils';
import { isTokenExpired } from '../../../services/authentication/utils';
import { getPartnerIdFromQuery, getPartnerNameFromQuery } from '../../../utils';
import { doCartTotalTtc, mapPartner, mapUserCart } from '../../../utils/cartUtils';

import Stepper from './Stepper';
import { Logo } from '../../icons/Logo';
import SvgComponent from '../../SvgComponent';
import HeadComponent from '../../HeadComponent';
import LocalizedNavLink from '../../router/LocalizedNavLink';

import EbonErrorModal from '../../funnel/EbonErrorModal';
import ScriptContainer from '../../funnel/ScriptContainer';
import FunnelLeftContainer from '../../funnel/FunnelLeftContainer';
import FunnelRightContainer from '../../funnel/FunnelRightContainer';
import { CheckoutFlowProvider } from '../../funnel/payment-step/payment-methods/checkout-flow/CheckoutFlowContext';

import './ValidationEbonPageStyle.scss';

const ValidationEbonPage = ({
    i18n,
    token,
    isAuthenticated,
    ebon,
    userCart,
    order,
    history,
    partner,
    queryPartnerId,
    queryPartnerName,
    showModal,
    logoutUser,
    fetchUserBalance,
    resetLock,
    resetKyc,
    resetOrder
}) => {
    const [userInfo, setUserInfo] = useState({});
    const [modalData, setModalData] = useState(null);
    const [paymentType, setPaymentType] = useState();
    const [stateOfTransaction, setStateOfTransaction] = useState(FUNNEL_ADDRESS_STEP);
    const [kycNeeded, setKycNeeded] = useState(false);

    const partnerId = partner ? partner.id : queryPartnerId;
    const partnerName = partner ? partner.name : queryPartnerName;
    const merchantCardPath = getMerchantCardUrl({ id: partnerId, name: partnerName }, false, true);

    const amountCashback = getCashbackAmount(ebon.ebons);
    const cartTotalValue = doCartTotalTtc(userCart, amountCashback);

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    useEffect(() => {
        if (!isMemberAuthenticated()) {
            new Promise((resolve, reject) => {
                history.push(merchantCardPath);
                resolve();
            }).then(() => logoutUser(() => showModal(MODAL_TYPE_LOGIN)));
        }

        if (userCart.length === 0) {
            history.push(merchantCardPath);
        }
    });

    useEffect(() => {
        fetchUserBalance();

        return () => {
            resetLock();
            resetKyc();
            resetOrder();
            setPaymentType(null);
            setStateOfTransaction(FUNNEL_ADDRESS_STEP);
        };
    }, []);

    return (
        <div className="template template-primary flex-container-v">
            <ScriptContainer />
            <div className="entete hideNav" id="entete">
                <div className="flex-container-v">
                    <header className="tetedepage conteneur flex-container-h flex-center-alt">
                        <LocalizedNavLink to={PATH.HOME_PATH} title={APP_NAME} className="logo">
                            <Logo />
                        </LocalizedNavLink>
                        <Stepper
                            steps={i18n.STEPS}
                            current={order.orderState === 'created' ? 2 : 1}
                        />
                    </header>
                </div>
            </div>

            <main id="ebon-page-container" className="conteneur conteneur-mobile">
                <HeadComponent title={i18n.TITLE} description={i18n.DESCRIPTION} />

                <Stepper
                    steps={i18n.STEPS}
                    current={order.orderState === 'created' ? 2 : 1}
                    cssClass="mobileDisplay"
                />

                <div className="goBack">
                    <LocalizedNavLink
                        to={merchantCardPath}
                        className="fakelink"
                        title={i18n.GO_BACK}>
                        <SvgComponent src={`${STATIC_ROOT}/chevron.svg`} width="12" height="12" />
                        {i18n.GO_BACK}
                    </LocalizedNavLink>
                </div>

                {!modalData ? (
                    <CheckoutFlowProvider>
                        <div className="mainContainer">
                            <FunnelLeftContainer
                                partnerId={partnerId}
                                userInfo={userInfo}
                                setUserInfo={setUserInfo}
                                paymentType={paymentType}
                                setPaymentType={setPaymentType}
                                cartTotalValue={cartTotalValue}
                                stateOfTransaction={stateOfTransaction}
                                setStateOfTransaction={setStateOfTransaction}
                                setModalData={setModalData}
                                merchantCardPath={merchantCardPath}
                                kycNeeded={kycNeeded}
                                setKycNeeded={setKycNeeded}
                            />

                            <FunnelRightContainer
                                partnerId={partnerId}
                                userInfo={userInfo}
                                userCart={userCart}
                                stateOfTransaction={stateOfTransaction}
                                setStateOfTransaction={setStateOfTransaction}
                                amountCashback={amountCashback}
                                cartTotalValue={cartTotalValue}
                                paymentType={paymentType}
                                kycNeeded={kycNeeded}
                            />
                        </div>
                    </CheckoutFlowProvider>
                ) : (
                    <EbonErrorModal
                        modalData={modalData}
                        merchantCardPath={merchantCardPath}
                        partnerName={partnerName}
                    />
                )}
            </main>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => {
    const queryPartnerId = getPartnerIdFromQuery(ownProps.match.params.query);
    const queryPartnerName = getPartnerNameFromQuery(ownProps.match.params.query);

    return {
        i18n: state.i18n.page.EBON,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated,
        ebon: state.ebon,
        order: state.ebon.createdOrder,
        userCart: mapUserCart(state.ebon.cart, state.ebon.ebonPartners),
        partner: mapPartner(state.ebon.cart, state.ebon.ebonPartners),
        queryPartnerId: queryPartnerId,
        queryPartnerName: queryPartnerName
    };
};

const mapDispatchToProps = {
    showModal,
    logoutUser,
    fetchUserBalance,
    resetLock,
    resetKyc,
    resetOrder
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ValidationEbonPage));



// WEBPACK FOOTER //
// ./src/components/pages/ebon/ValidationEbonPage.js